import { Injectable } from '@angular/core';
import { UserService } from '@trp/community/user/feature-user';
import { Observable, combineLatest, map } from 'rxjs';
import { MenuDefinition } from './menu.model';
import { DEFAULT_MENU_CONFIG, STATE_MENU_CONFIGS } from './menu.config';
import { USAStateEnum } from '@trp/community/auth/interfaces';

@Injectable({ providedIn: 'root' })
export class MenuService {
  constructor(private userService: UserService) {}

  hasContactUsAccess$: Observable<boolean> = this.userService.me$.pipe(
    map((user) => {
      const userState = user?.state?.toLowerCase() || '';
      if (this.isUSAStateEnum(userState)) {
        const menuConfig =
          STATE_MENU_CONFIGS[userState as USAStateEnum] ?? DEFAULT_MENU_CONFIG;
        return menuConfig.items.some((item) => item.link === 'contact-us');
      }
      return true; // Default config has Contact Us
    })
  );

  getMenu(): Observable<MenuDefinition> {
    return combineLatest([
      this.userService.me$,
      this.userService.isVerifiedUser$,
      this.userService.isAdmin$,
      this.userService.isInEducationalMaterialsState$,
      this.userService.hasAccessToEducationalMaterials$,
    ]).pipe(
      map(([user, isVerifiedUse, isAdmin, isInEducationalMaterialsState, hasAccessToEducationalMaterials]) => {
        const userState = user?.state.toLowerCase() || '';
        let state: USAStateEnum | undefined;
        if (this.isUSAStateEnum(userState)) {
          state = userState;
        }

        const menuDefinition =
          STATE_MENU_CONFIGS[state as USAStateEnum] ?? DEFAULT_MENU_CONFIG;

        return {
          ...menuDefinition,
          items: menuDefinition.items
            .filter(
              (menuItem) =>
                (!menuItem.onlyAdmin || isAdmin) &&
                (!menuItem.onlyCommunityManager || user?.isCommunityManager)
            )
            .map((menuItem) => {
              return {
                ...menuItem,
                ...(menuItem.onlyVerified && {
                  verified: !!isVerifiedUse,
                }),
                ...(menuItem.onlyCAA && {
                  verified: hasAccessToEducationalMaterials || isAdmin,
                }),
                ...(menuItem.onlyCommunityManager && {
                  communityManager: user?.isCommunityManager,
                }),
              };
            }),
        };
      })
    );
  }

  getPublicMenu(): MenuDefinition {
    return {
      ...DEFAULT_MENU_CONFIG,
      items: DEFAULT_MENU_CONFIG.items.filter(
        (item) =>
          !item.onlyVerified &&
          !item.onlyCAA &&
          !item.onlyAdmin &&
          !item.onlyCommunityManager
      ),
    };
  }

  getStateMenu(state: USAStateEnum | undefined): MenuDefinition {
    const menuDefinition =
      STATE_MENU_CONFIGS[state as USAStateEnum] ?? DEFAULT_MENU_CONFIG;
    return {
      ...menuDefinition,
      items: menuDefinition.items.filter(
        (item) =>
          !item.onlyVerified &&
          !item.onlyCAA &&
          !item.onlyAdmin &&
          !item.onlyCommunityManager &&
          !item.pinnedBottom
      ),
    };
  }

  private isUSAStateEnum(value: string): value is USAStateEnum {
    return Object.values(USAStateEnum).includes(value as USAStateEnum);
  }
}
